import React, { useState } from 'react';
import { Modal, Table, Button, Tag, Select, Space, Tooltip } from 'antd';
import { Plus, Clock } from 'lucide-react';
import { useFeedbackStore } from '~/store/feedback.store';
import { useGetMyFeedback } from '~/services/feedback/feedback.hooks';
import { TFeedback } from '~/types/feedback';
import type { ColumnsType } from 'antd/es/table';
import { NewFeedbackForm } from './new-feedback-form';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'OPEN': return 'red';
    case 'IN_PROGRESS': return 'orange';
    case 'COMPLETED': return 'green';
    default: return 'default';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const ClientFeedbackModal: React.FC = () => {
  const { isModalOpen, closeModal, openNewFeedback } = useFeedbackStore();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const { data: feedbackData, isLoading } = useGetMyFeedback({
    pageSize: 50,
    status: statusFilter as any,
  });

  const columns: ColumnsType<TFeedback> = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '50%',
      render: (text: string) => (
        <div style={{ maxWidth: 400, wordWrap: 'break-word' }}>
          {text.length > 120 ? (
            <Tooltip title={text}>
              {text.substring(0, 120)}...
            </Tooltip>
          ) : (
            text
          )}
        </div>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date: string) => (
        <div className="flex items-center gap-1 text-xs">
          <Clock size={12} />
          {formatDate(date)}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.replace('_', ' ')}
        </Tag>
      ),
    },
    {
      title: 'Response',
      dataIndex: 'adminComments',
      key: 'adminComments',
      width: '25%',
      render: (comments: string) => (
        <div className="text-xs">
          {comments || <span className="text-gray-400">No response yet</span>}
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <div className="flex justify-between items-center">
            <span>My Feedback</span>
            <Space>
              <Select
                placeholder="Filter by status"
                allowClear
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 150 }}
              >
                <Select.Option value="OPEN">Open</Select.Option>
                <Select.Option value="IN_PROGRESS">In Progress</Select.Option>
                <Select.Option value="COMPLETED">Completed</Select.Option>
              </Select>
              <Button
                type="primary"
                icon={<Plus size={16} />}
                onClick={openNewFeedback}
              >
                New Feedback
              </Button>
            </Space>
          </div>
        }
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        width="80%"
        style={{ maxWidth: 900 }}
        destroyOnClose
      >
        <Table
          columns={columns}
          dataSource={feedbackData?.data?.items || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} submissions`,
          }}
          scroll={{ x: 600 }}
          size="middle"
          locale={{ emptyText: 'No feedback submitted yet. Click "New Feedback" to get started.' }}
        />
      </Modal>

      <NewFeedbackForm />
    </>
  );
};
